import React, { useState, useEffect } from 'react';
import { 
  Gift, Send, Users, Mail, Plus, Trash2, CheckCircle, 
  AlertCircle, Sparkles, Package, ArrowLeft, RefreshCw,
  UserPlus, Calendar, Eye
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import './GiftAdmin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = 'bHyyve2026';

const GiftAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('send');
  
  // Gift sending state
  const [gifts, setGifts] = useState([]);
  const [selectedGift, setSelectedGift] = useState('');
  const [recipientEmails, setRecipientEmails] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingGifts, setIsLoadingGifts] = useState(false);
  
  // New gift form
  const [newGift, setNewGift] = useState({ name: '', description: '', code: '' });
  const [isAddingGift, setIsAddingGift] = useState(false);
  
  // Gift history
  const [giftHistory, setGiftHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Load gift types from database
  const loadGiftTypes = async () => {
    setIsLoadingGifts(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/gift-types`, {
        headers: { 'X-Admin-Password': ADMIN_PASSWORD }
      });
      if (response.ok) {
        const data = await response.json();
        setGifts(data.giftTypes || []);
      }
    } catch (error) {
      console.error('Failed to load gift types:', error);
    } finally {
      setIsLoadingGifts(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Welcome to Gift Admin!');
      loadGiftTypes();
      loadGiftHistory();
    } else {
      toast.error('Invalid password');
    }
  };

  const loadGiftHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/gift-history`, {
        headers: { 'X-Admin-Password': ADMIN_PASSWORD }
      });
      if (response.ok) {
        const data = await response.json();
        setGiftHistory(data.history || []);
      }
    } catch (error) {
      console.error('Failed to load gift history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleAddGift = async () => {
    if (!newGift.name || !newGift.code) {
      toast.error('Please enter gift name and code');
      return;
    }
    
    setIsAddingGift(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/gift-types`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Password': ADMIN_PASSWORD
        },
        body: JSON.stringify(newGift)
      });
      
      if (response.ok) {
        const data = await response.json();
        setGifts([...gifts, data.giftType]);
        setNewGift({ name: '', description: '', code: '' });
        toast.success('Gift added and saved!');
      } else {
        toast.error('Failed to save gift');
      }
    } catch (error) {
      console.error('Error adding gift:', error);
      toast.error('Failed to add gift');
    } finally {
      setIsAddingGift(false);
    }
  };

  const handleRemoveGift = async (giftId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/gift-types/${giftId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': ADMIN_PASSWORD }
      });
      
      if (response.ok) {
        setGifts(gifts.filter(g => g.id !== giftId));
        if (selectedGift === giftId) setSelectedGift('');
        toast.success('Gift removed');
      } else {
        toast.error('Failed to remove gift');
      }
    } catch (error) {
      console.error('Error removing gift:', error);
      toast.error('Failed to remove gift');
    }
  };

  const handleSendGifts = async () => {
    if (!selectedGift) {
      toast.error('Please select a gift to send');
      return;
    }
    
    const emails = recipientEmails
      .split(/[\n,;]/)
      .map(e => e.trim())
      .filter(e => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    
    if (emails.length === 0) {
      toast.error('Please enter valid email addresses');
      return;
    }
    
    const gift = gifts.find(g => g.id === selectedGift);
    
    if (!gift) {
      toast.error('Please select a valid gift');
      return;
    }
    
    setIsSending(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/send-gifts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: ADMIN_PASSWORD,
          gift: {
            name: gift.name,
            description: gift.description || '',
            code: gift.code
          },
          recipientEmails: emails
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Gifts sent to ${data.sentCount} recipients!`);
        setRecipientEmails('');
        loadGiftHistory();
      } else {
        toast.error(data.detail || 'Failed to send gifts');
      }
    } catch (error) {
      console.error('Error sending gifts:', error);
      toast.error('Failed to send gifts');
    } finally {
      setIsSending(false);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="gadmin-page" data-testid="gift-admin-login">
        <div className="gadmin-login-container">
          <div className="gadmin-login-card">
            <Gift size={48} className="gadmin-login-icon" />
            <h1>Gift Admin</h1>
            <p>Enter password to manage customer gifts</p>
            <form onSubmit={handleLogin}>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="gadmin-password-input"
                data-testid="admin-password-input"
              />
              <Button type="submit" className="gadmin-login-btn" data-testid="admin-login-btn">
                Access Gift Admin
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gadmin-page" data-testid="gift-admin-page">
      {/* Header */}
      <header className="gadmin-header">
        <div className="gadmin-container">
          <div className="gadmin-header-content">
            <a href="/" className="gadmin-logo-link">
              <img src="/logo.svg" alt="bHyyve" className="gadmin-logo" />
              <span className="gadmin-logo-text">bHyyve</span>
            </a>
            <div className="gadmin-header-title">
              <Gift size={24} />
              <span>Gift Admin</span>
            </div>
            <a href="/admin" className="gadmin-back-link">
              <ArrowLeft size={18} />
              Back to Admin
            </a>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="gadmin-tabs">
        <div className="gadmin-container">
          <button 
            className={`gadmin-tab ${activeTab === 'send' ? 'active' : ''}`}
            onClick={() => setActiveTab('send')}
          >
            <Send size={18} />
            Send Gifts
          </button>
          <button 
            className={`gadmin-tab ${activeTab === 'manage' ? 'active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            <Package size={18} />
            Manage Gifts
          </button>
          <button 
            className={`gadmin-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => { setActiveTab('history'); loadGiftHistory(); }}
          >
            <Calendar size={18} />
            History
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="gadmin-main">
        <div className="gadmin-container">
          
          {/* Send Gifts Tab */}
          {activeTab === 'send' && (
            <div className="gadmin-section" data-testid="send-gifts-section">
              <div className="gadmin-section-header">
                <h2><Sparkles size={24} /> Send Gifts to Customers</h2>
                <p>Select a gift and enter customer emails to send rewards</p>
              </div>
              
              <div className="gadmin-send-grid">
                {/* Gift Selection */}
                <div className="gadmin-card">
                  <h3><Gift size={20} /> Select Gift</h3>
                  <div className="gadmin-gift-list">
                    {isLoadingGifts ? (
                      <div className="gadmin-loading-inline">Loading gifts...</div>
                    ) : gifts.length === 0 ? (
                      <div className="gadmin-empty-inline">
                        <p>No gift types created yet.</p>
                        <p>Go to "Manage Gifts" tab to add some!</p>
                      </div>
                    ) : (
                      gifts.map(gift => (
                        <label 
                          key={gift.id} 
                          className={`gadmin-gift-option ${selectedGift === gift.id ? 'selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name="gift"
                            value={gift.id}
                            checked={selectedGift === gift.id}
                            onChange={(e) => setSelectedGift(e.target.value)}
                          />
                          <div className="gadmin-gift-info">
                            <span className="gadmin-gift-name">{gift.name}</span>
                            <span className="gadmin-gift-desc">{gift.description}</span>
                            <span className="gadmin-gift-code">Code: {gift.code}</span>
                          </div>
                          <CheckCircle size={20} className="gadmin-gift-check" />
                        </label>
                      ))
                    )}
                  </div>
                </div>
                
                {/* Email List */}
                <div className="gadmin-card">
                  <h3><Users size={20} /> Recipient Emails</h3>
                  <p className="gadmin-helper" style={{color: '#fbbf24', fontWeight: 'bold'}}>
                    👇 Click below and type email addresses to send gifts
                  </p>
                  <textarea
                    value={recipientEmails}
                    onChange={(e) => setRecipientEmails(e.target.value)}
                    placeholder="Type email addresses here..."
                    className="gadmin-email-textarea"
                    rows={8}
                    data-testid="recipient-emails-input"
                  />
                  <div className="gadmin-email-count" style={{color: recipientEmails.trim() ? '#10b981' : '#ef4444'}}>
                    {recipientEmails.split(/[\n,;]/).filter(e => e.trim()).length} email(s) entered
                    {!recipientEmails.trim() && ' - Please type email addresses above!'}
                  </div>
                </div>
              </div>
              
              <Button 
                className="gadmin-send-btn"
                onClick={handleSendGifts}
                disabled={isSending || !selectedGift || !recipientEmails.trim()}
                data-testid="send-gifts-btn"
              >
                {isSending ? (
                  <>
                    <RefreshCw size={20} className="gadmin-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Send Gifts to All Recipients
                  </>
                )}
              </Button>
            </div>
          )}
          
          {/* Manage Gifts Tab */}
          {activeTab === 'manage' && (
            <div className="gadmin-section" data-testid="manage-gifts-section">
              <div className="gadmin-section-header">
                <h2><Package size={24} /> Manage Gift Types</h2>
                <p>Add or remove gift options that can be sent to customers</p>
              </div>
              
              {/* Add New Gift */}
              <div className="gadmin-card gadmin-add-gift">
                <h3><Plus size={20} /> Add New Gift</h3>
                <div className="gadmin-add-form">
                  <div className="gadmin-form-row">
                    <Input
                      value={newGift.name}
                      onChange={(e) => setNewGift({...newGift, name: e.target.value})}
                      placeholder="Gift Name (e.g., 20% Off)"
                      data-testid="new-gift-name"
                    />
                    <Input
                      value={newGift.code}
                      onChange={(e) => setNewGift({...newGift, code: e.target.value.toUpperCase()})}
                      placeholder="Gift Code (e.g., SAVE20)"
                      data-testid="new-gift-code"
                    />
                  </div>
                  <Input
                    value={newGift.description}
                    onChange={(e) => setNewGift({...newGift, description: e.target.value})}
                    placeholder="Description (optional)"
                    data-testid="new-gift-description"
                  />
                  <Button onClick={handleAddGift} className="gadmin-add-btn" disabled={isAddingGift}>
                    {isAddingGift ? <RefreshCw size={18} className="gadmin-spin" /> : <Plus size={18} />}
                    {isAddingGift ? 'Saving...' : 'Add Gift'}
                  </Button>
                </div>
              </div>
              
              {/* Existing Gifts */}
              <div className="gadmin-card">
                <h3><Gift size={20} /> Current Gift Options ({gifts.length})</h3>
                <div className="gadmin-existing-gifts">
                  {gifts.length === 0 ? (
                    <div className="gadmin-empty-inline">No gifts created yet. Add your first gift above!</div>
                  ) : (
                    gifts.map(gift => (
                      <div key={gift.id} className="gadmin-existing-gift">
                        <div className="gadmin-existing-info">
                          <span className="gadmin-existing-name">{gift.name}</span>
                          <span className="gadmin-existing-desc">{gift.description}</span>
                          <span className="gadmin-existing-code">{gift.code}</span>
                        </div>
                        <button 
                          className="gadmin-remove-btn"
                          onClick={() => handleRemoveGift(gift.id)}
                          title="Remove gift"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="gadmin-section" data-testid="gift-history-section">
              <div className="gadmin-section-header">
                <h2><Calendar size={24} /> Gift Send History</h2>
                <p>Track all gifts sent and their share status</p>
                <Button onClick={loadGiftHistory} className="gadmin-refresh-btn">
                  <RefreshCw size={18} className={isLoadingHistory ? 'gadmin-spin' : ''} />
                  Refresh
                </Button>
              </div>
              
              {isLoadingHistory ? (
                <div className="gadmin-loading">Loading history...</div>
              ) : giftHistory.length === 0 ? (
                <div className="gadmin-empty">
                  <Gift size={48} />
                  <p>No gifts have been sent yet</p>
                </div>
              ) : (
                <div className="gadmin-history-list">
                  {giftHistory.map((item, index) => (
                    <div key={index} className="gadmin-history-item">
                      <div className="gadmin-history-gift">
                        <Gift size={20} />
                        <span>{item.giftName}</span>
                      </div>
                      <div className="gadmin-history-recipient">
                        <Mail size={16} />
                        <span>{item.recipientEmail}</span>
                      </div>
                      <div className="gadmin-history-date">
                        {new Date(item.sentAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      <div className={`gadmin-history-status ${item.shared ? 'shared' : ''}`}>
                        {item.shared ? (
                          <>
                            <UserPlus size={16} />
                            Shared
                          </>
                        ) : (
                          <>
                            <Eye size={16} />
                            Sent
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default GiftAdmin;
